<template>
  <div class="element__wrapper" :class="elementWrapperClass">

    <div v-if="elementProps && !isHidden && elementProps.introText" class="element__intro">
      <div v-if="elementProps && !isHidden && onchainidEnabled" class="element__onchainid-logo">
        <span class="_text:xs _text(500)">Integrated wallet</span>
      </div>
      <p class="_text:xs _text-grey-900">{{elementProps.introText}}</p>
    </div>
    <div class="element" :class="{'_flex':  isInputCheckBox}" >
      <ds ds="tooltip"
          :class="tooltipClass"
          :isVisible="isTooltipVisible"
          size="xs"
          :text="!isInputCheckBox ? elementProps.helperText : undefined"
        >
        <ds :ds="elementProps.type"
          v-if="elementProps && !isHidden"
          :name="elementProps.key"
          :label="elementProps.label"
          :value.sync="elementValues[elementProps.key]"
          :validate="elementProps.validate"

          :isDisabled="isDisabled"
          :isRequired="elementProps.isRequired"
          :isReadonly="readonly || isForcedReadonly"
          :min="elementProps.min"
          :max="elementProps.max"
          :isSearchable="elementProps.isSearchable"
          :options="elementProps.options"
          :checkedValue="elementProps.checkedValue"
          :initialPosition="elementProps.value || undefined"

          :mimetype="elementProps.mimeTypes"
          :maxsize="elementProps.maxsize || elementProps.maxSize"

          @focusin="isTooltipVisible = true"
          @focusout="isTooltipVisible = false"
          returnType="alpha3"
        >
          <div slot="helperText" v-if="isInputCheckBox" v-html="helperText" />
          <div v-if="elementProps.type === 'select'" slot="value">
            <span v-if="elementValues[elementProps.key]">
              <ds ds="icon"
                v-if="elementProps.options.find(option => option.value === elementValues[elementProps.key]).isVerified"
                name="verified"
                color="var(--success)"
                size="0.8rem"
                class="_mright-2"
              />
              {{ elementProps.options.find(option => option.value === elementValues[elementProps.key]).label }}
            </span>
          </div>
          <div v-if="elementProps.type === 'select'" slot="options">
            <ds ds="select-option"
              v-for="option of elementProps.options"
              :key="option.value"
              :value="option.value"
            >
              <div>
                <ds ds="icon"
                  v-if="option.isVerified"
                  name="verified"
                  color="var(--success)"
                  size="0.8rem"
                  class="_mright-2"
                />
                {{ option.label }}
              </div>
            </ds>
          </div>
        </ds>
      </ds>

      <ds
        ds="button"
        v-if="elementProps.additionalInfo"
        @click="$refs.additionalInfoDialog.$el.open();"
        :class="`element__info-button element__info-button--${elementProps.type}`"
        icon="help"
        iconSize="18px"
        mode="text"
      />
      <ds ds="dialog"
        v-if="elementProps.additionalInfo"
        ref="additionalInfoDialog"
        :isModal="true"
        :isDismissible="true"
      >
        <component :is="dialogContent"  @close="$refs.additionalInfoDialog.$el.close()" />
      </ds>
    </div>
  </div>
</template>
<script>

import { eventBus } from '@/eventBus';
import { evaluateCondition } from '@/utils';
import onchainidLogo from '@/assets/images/onchainid.png';

export default {
  name: 'SimpleElement',
  data: () => ({
    elementProps: null,
    onchainidEnabled: false,
    onchainidLogo,
    fieldsetDisabled: false,
    fieldsetHidden: false,
    isTooltipVisible: false,
    isForcedReadonly: false
  }),
  props: ['element', 'elementValues', 'readonly', 'fieldsetId', 'sectionId'],
  computed: {
    isDisabled () {
      if (this.fieldsetDisabled) return true;
      return evaluateCondition(this.element.disabledWhen, this.elementValues);
    },
    isHidden () {
      if (this.fieldsetHidden) return true;
      return evaluateCondition(this.element.hiddenWhen, this.elementValues);
    },
    isHiddenOrDisabled () {
      return this.isHidden || this.isDisabled || false;
    },
    isInputCheckBox () {
      return this.elementProps.type === 'input-checkbox';
    },
    dialogContent () {
      if (!this.element.additionalInfo) return;
      const dialogComponent = this.element.additionalInfo.charAt(0).toUpperCase() + this.element.additionalInfo.slice(1);
      return () => import('../dialogs/' + dialogComponent);
    },
    tooltipClass () {
      return {
        element__checkbox__tooltip: this.isInputCheckBox,
        'element__checkbox__tooltip--agreement': this.sectionId === 'agreements'
      };
    },
    elementWrapperClass () {
      return {
        'element__wrapper--details': this.elementProps.id === 'details',
        'element__wrapper--wallet': this.elementProps.id === 'select_wallet'
      };
    },
    helperText () {
      if (typeof this.elementProps.helperText === 'string' || this.elementProps.helperText instanceof String) {
        return this.elementProps.helperText.replace(/<a ([^>]*)href=(['"])([^\2]+?\2)/g, (_, m1, m2, m3) => `<a ${m1}target="_blank" href=${m2 + m3}`);
      }
      return this.elementProps.helperText;
    }
  },
  watch: {
    isHiddenOrDisabled: {
      immediate: true,
      handler (currentState, previousState) {
        const id = this.elementProps ? this.elementProps.key : this.element.name || this.element.id;
        if (currentState) {
          eventBus.$emit('elementDetached', { id });
        } else if (previousState && !currentState) {
          eventBus.$emit('elementAttached', { id });
        }
      }
    },
    element: {
      immediate: true,
      handler () {
        this.elementProps = this.mapType(this.element);
      }
    },
    isHidden () {
      if (this.isHidden) {
        // emulate old design-system reset on disconnect functionality
        this.elementValues[this.elementProps.key] = null;
      }
    }
  },
  methods: {

    mapType (el) {
      el.key = el.name || el.id;

      el.isRequired = el.required;
      if (el.required) el.label += ' *';

      switch (el.type) {
        // special case of input-string
        case 'input-email':
          el.type = 'input-string';
          if (el.required) {
            el.validate = this.ds.vEmail;
          } else {
            el.validate = this.ds.vEmptyOr(this.ds.vEmail);
          }
          return el;

        // special case of input-checkbox
        case 'checkbox-onchainid':
          el.type = 'input-checkbox';
          this.onchainidEnabled = true;
          if (el.required) {
            el.validate = this.ds.vTrue;
          }
          return el;

        // more standard schema types
        case 'input-select':
        case 'select-simple':
          el.type = 'select';
          el.options = el.options.sort((a, b) => a.label.localeCompare(b.label));
          if (el.required) {
            el.validate = this.ds.vRequired;
          }

          return el;

        case 'input-checkbox':
          if (el.required) {
            el.validate = this.ds.vTrue;
          }
          return el;

        case 'input-date':
          el.type = 'input-date-picker';
          // trim time portion off datetime string
          el.value = el.value ? el.value.substr(0, 10) : null;
          this.elementValues[el.id] = el.value;
          if (el.minDate) el.min = el.minDate.substr(0, 10);
          if (el.maxDate) el.max = el.maxDate.substr(0, 10);
          return el;

        case 'input-string':
          if (el.required) {
            if (this.element.minLength) {
              el.validate = (v) =>
                this.ds.vRequired(v) ||
                this.ds.vMinLength(this.element.minLength)(v) ||
                this.ds.vMaxLength(this.element.maxLength || 255)(v);
            } else {
              el.validate = (v) =>
                this.ds.vRequired(v) ||
                this.ds.vMaxLength(this.element.maxLength || 255)(v);
            }
          } else {
            el.validate = this.ds.vEmptyOr(v =>
              this.ds.vMinLength(this.element.minLength || 0)(v) ||
              this.ds.vMaxLength(this.element.maxLength || 255)(v)
            );
          }
          return el;

        case 'input-number':
          if (this.element.max) {
            el.validate = (v) =>
              this.ds.vDecimal(this.element.decimals || 0)(v) ||
              this.ds.vMinNumber(this.element.min || 0)(v) ||
              this.ds.vMaxNumber(this.element.max)(v);
          } else {
            el.validate = (v) =>
              this.ds.vDecimal(this.element.decimals || 0)(v) ||
              this.ds.vMinNumber(this.element.min || 0)(v);
          }
          return el;

        default:
          return el;
      }
    },

    handleFieldsetDisabled ({ fieldsetId, isDisabled }) {
      if (this.fieldsetId && this.fieldsetId === fieldsetId) {
        this.fieldsetDisabled = isDisabled;
      }
    },
    handleFieldsetHidden ({ fieldsetId, isHidden }) {
      if (this.fieldsetId && this.fieldsetId === fieldsetId) {
        this.fieldsetHidden = isHidden;
      }
    }
  },
  created () {
    if (this.fieldsetId) {
      eventBus.$on('fieldsetDisabled', this.handleFieldsetDisabled);
      eventBus.$on('fieldsetHidden', this.handleFieldsetHidden);
    }
  }
};
</script>

<style lang="less" scoped>

.element {
  position: relative;

  &__wrapper {
    width: 100%;
    &--details {
      overflow-x: clip
    }
    &--wallet {
      word-break: break-all;
    }
  }

  &__intro {
    padding: calc(var(--gw) * 1.2) calc(var(--gw) * 1.5);
    margin: calc(var(--gw) * 1.5) 0 calc(var(--gw) * 2);
    background: var(--grey-100);
  }

  &__onchainid-logo {
    display: flex;
    align-items: center;
    margin-bottom: calc(var(--gw) * 0.5);
  }

  &__info-button {
    &--input-checkbox {
      margin-top: calc(var(--gw) * -0.3);
      width: calc(var(--gw) * 1);
      transform: translateX(calc(var(--gw) * -1.5));
      padding: 0 calc(var(--gw) * .5);
    }

    &--input-radio {
      top: -0.3rem;
      right: 0;
    }
  }
  &__checkbox {
    &__tooltip {
      flex-grow: 1;
      &--agreement {
        z-index: 3000
      }
    }
  }

  &__text-divider {
    --text-divider-gap: 1rem;
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--grey-800);

    &:before, &:after {
      content: '';
      height: 1px;
      background-color: var(--grey-500);
      flex-grow: 1;
    }

    &:before {
      margin-right: var(--text-divider-gap);
    }

    &:after {
      margin-left: var(--text-divider-gap);
    }
  }
}

</style>
